import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Layers, ShieldCheck, ArrowDownRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { scrollToId } from "@/lib/api";

/* NaN-safe radius guard — invalid geometry renders nothing instead of console errors */
const rr = (v) => (Number.isFinite(v) ? v : 0.1);

const REF_IMG = "/files/ht-cable-construction.webp";

const FAMILIES = {
    "11kv": {
        label: "11 kV",
        grade: "11 kV",
        voltage: "11 kV (Um 12 kV)",
        size: "400 sqmm × 3 Core",
        shortSize: "400",
        note: "The distribution workhorse for utilities, industries and infrastructure projects.",
    },
    "33kv": {
        label: "33 kV",
        grade: "33 kV",
        voltage: "33 kV (Um 36 kV)",
        size: "300 sqmm × 3 Core",
        shortSize: "300",
        note: "Reference construction — we stock Raychem termination kits up to 33 kV; 33 kV cables supplied on indent.",
    },
};

const TYPES = [
    {
        key: "fsa",
        tab: "Flat Strip Armour",
        short: "FSA",
        nickname: "The Workhorse",
        armourTitle: "Flat Strip Armour",
        desc: "Used where mechanical protection with flat steel strip armour is sufficient. The common choice for underground installations.",
        armour: "Flat galvanised steel strip",
        sheath: "PVC / PE",
        strength: "Good",
        flex: "Low",
        moisture: "Standard (PVC / PE)",
        best: "Everyday underground & infrastructure runs",
        app: "Underground · Industrial · Infrastructure",
        sheathDesc: "Standard PVC / PE outer jacket — seals the build against UV, abrasion and site handling.",
        armourDesc: "Flat galvanised steel strip wound around the bedding — sufficient mechanical protection for everyday underground runs.",
    },
    {
        key: "rwa",
        tab: "Round Wire Armour",
        short: "RWA",
        nickname: "The Strong One",
        armourTitle: "Round Wire Armour",
        desc: "Used where higher mechanical strength and flexibility is required — underground, industrial and critical installations.",
        armour: "Round galvanised steel wires",
        sheath: "PVC / PE",
        strength: "High",
        flex: "High",
        moisture: "Standard (PVC / PE)",
        best: "Critical routes needing strength + flexibility",
        app: "Underground · Industrial · High mechanical strength",
        sheathDesc: "Standard PVC / PE outer jacket — seals the build against UV, abrasion and site handling.",
        armourDesc: "Round galvanised steel wires — noticeably higher mechanical strength with the flexibility to follow bends in the route.",
    },
    {
        key: "rwape",
        tab: "RWA + Pressure Sheath",
        short: "RWA·PE",
        nickname: "The All-Weather One",
        armourTitle: "RWA with Pressure-Extruded Sheath",
        desc: "Built for harsh environments needing extra moisture, chemical and mechanical protection. Pressure extrusion gives a seamless, high-quality outer sheath with better durability.",
        armour: "Round galvanised steel wires",
        sheath: "Pressure-extruded PE — seamless",
        strength: "High",
        flex: "High",
        moisture: "Superior — seamless PE resists water & chemicals",
        best: "Harsh, wet or chemical-exposed sites",
        app: "Harsh conditions · High-moisture areas · Critical installations",
        sheathDesc: "Pressure-extruded PE — a seamless, high-quality outer sheath with superior water and chemical resistance for harsh sites.",
        armourDesc: "Round galvanised steel wires — high mechanical strength with the flexibility to follow bends in the route.",
    },
];

const MATRIX = [
    { label: "Armour Type", values: ["Flat steel strip", "Round steel wires", "Round steel wires"] },
    { label: "Mechanical Strength", values: ["Good", "High", "High"] },
    { label: "Flexibility", values: ["Low", "High", "High"] },
    { label: "Moisture & Chemical", values: ["Standard PVC / PE", "Standard PVC / PE", "Seamless extruded PE — superior"] },
    { label: "Best For", values: ["Everyday underground runs", "Critical high-strength routes", "Harsh, wet, chemical sites"] },
];

const CORE_POS = [
    [200, 138],
    [146, 231],
    [254, 231],
];

const layerInfo = (t, fam) => [
    { id: "sheath", name: "Outer Sheath", desc: t.sheathDesc },
    { id: "armour", name: `Armour — ${t.short}`, desc: t.armourDesc },
    { id: "bedding", name: "Bedding & Inner Sheath", desc: "Extruded bedding cushions the cores, and the inner sheath binds all three into one round, water-tight assembly." },
    { id: "insulation", name: "XLPE Insulation + Screens", desc: `${fam.grade} grade cross-linked polyethylene around each core, sandwiched between semiconducting conductor and core screens that keep the electric field perfectly even.` },
    { id: "conductor", name: "Stranded Conductor", desc: `${fam.size.split(" ")[0]} sqmm per core — stranded annealed copper (or aluminium), Class 2. The actual current path.` },
];

/* Realistic HT 3-core XLPE build, inside out per core:
   stranded conductor → semiconducting conductor screen → XLPE insulation →
   semiconducting insulation screen → copper tape screen; then fillers, bedding,
   armour (flat strip or round wire) and outer sheath. */
const HT_SHEATH = { outer: 190, inner: 168 };
const HT_ARMOUR = { ring: 157, wireR: 8 };
const HT_BEDDING = { outer: 146, inner: 128 };
const HT_CORE_R = 46; // copper tape screen outer radius per core

const htArmourWires = Array.from({ length: 54 }, (_, i) => {
    const rad = (i / 54) * 2 * Math.PI;
    return { x: 200 + HT_ARMOUR.ring * Math.cos(rad), y: 200 + HT_ARMOUR.ring * Math.sin(rad), key: i };
});
const HT_FILLERS = [
    { x: 200, y: 200, r: 13 },
    ...[30, 150, 270].map((deg) => {
        const rad = ((deg - 90) * Math.PI) / 180;
        return { x: 200 + 106 * Math.cos(rad), y: 200 + 106 * Math.sin(rad), r: 15 };
    }),
];
const htStrands = (cx, cy, r, ring) =>
    [{ x: cx, y: cy }, ...[0, 60, 120, 180, 240, 300].map((deg) => {
        const rad = ((deg - 90) * Math.PI) / 180;
        return { x: cx + ring * Math.cos(rad), y: cy + ring * Math.sin(rad), r };
    })];

function HTSection({ typeKey, thick, active, onHover }) {
    const condR = thick ? 19 : 26; // 33 kV runs a thicker insulation wall
    const strandR = thick ? 6.5 : 9;
    const strandRing = thick ? 12.5 : 17;
    const dim = (id) => (active === id ? 1 : 0.82);

    const ring = (id, mid, w) => (
        <circle
            key={id}
            cx="200"
            cy="200"
            r={rr(mid)}
            fill="none"
            stroke={active === id ? "#F0A35E" : "transparent"}
            strokeOpacity="0.7"
            strokeWidth={w}
            strokeDasharray={active === id ? "7 5" : undefined}
            className="transition-all duration-300 cursor-pointer"
            onMouseEnter={() => onHover(id)}
            onClick={() => onHover(id)}
            style={{ pointerEvents: "stroke" }}
            data-testid={`ht-layer-${id}`}
        />
    );

    return (
        <svg viewBox="0 0 400 400" className="w-full h-full" data-testid="ht-live-svg">
            <defs>
                <radialGradient id="htSheath" cx="36%" cy="30%" r="85%">
                    <stop offset="0%" stopColor="#3A4152" />
                    <stop offset="45%" stopColor="#1B1E28" />
                    <stop offset="100%" stopColor="#0B0D13" />
                </radialGradient>
                <radialGradient id="htSteel" cx="35%" cy="30%" r="80%">
                    <stop offset="0%" stopColor="#E8ECF2" />
                    <stop offset="55%" stopColor="#9AA2B0" />
                    <stop offset="100%" stopColor="#565E6E" />
                </radialGradient>
                <radialGradient id="htCu" cx="35%" cy="30%" r="80%">
                    <stop offset="0%" stopColor="#F0B67F" />
                    <stop offset="55%" stopColor="#C56B2E" />
                    <stop offset="100%" stopColor="#7A3B12" />
                </radialGradient>
                <radialGradient id="htXlpe" cx="40%" cy="34%" r="80%">
                    <stop offset="0%" stopColor="#EFE8D8" />
                    <stop offset="100%" stopColor="#C9BFA8" />
                </radialGradient>
                <radialGradient id="htSheen" cx="34%" cy="28%" r="75%">
                    <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.10" />
                    <stop offset="45%" stopColor="#FFFFFF" stopOpacity="0.02" />
                    <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
                </radialGradient>
            </defs>

            {/* outer sheath */}
            <circle cx="200" cy="200" r={rr(HT_SHEATH.outer)} fill="url(#htSheath)" stroke="#05070B" strokeWidth="2" opacity={dim("sheath")} className="transition-opacity duration-300" />
            <circle cx="200" cy="200" r={rr(HT_SHEATH.outer - 4)} fill="none" stroke="#FFFFFF" strokeOpacity="0.05" strokeWidth="2" strokeDasharray="2 7" />
            {typeKey === "rwape" && (
                <circle cx="200" cy="200" r={rr(HT_SHEATH.outer - 9)} fill="none" stroke="#60A5FA" strokeWidth="3" strokeOpacity="0.55" />
            )}

            {/* armour */}
            <g opacity={dim("armour")} className="transition-opacity duration-300">
                {typeKey === "fsa" ? (
                    <>
                        <circle cx="200" cy="200" r={rr(HT_ARMOUR.ring)} fill="none" stroke="#6B7383" strokeWidth={HT_ARMOUR.wireR * 2.4} strokeDasharray="34 3" opacity="0.9" />
                        <circle cx="200" cy="200" r={rr(HT_ARMOUR.ring)} fill="none" stroke="#424A5A" strokeWidth={HT_ARMOUR.wireR * 2.4} strokeDasharray="34 3" strokeDashoffset="18" opacity="0.85" />
                    </>
                ) : (
                    htArmourWires.map((w) => (
                        <circle key={w.key} cx={w.x} cy={w.y} r={rr(HT_ARMOUR.wireR)} fill="url(#htSteel)" stroke="#2E3542" strokeWidth="1" />
                    ))
                )}
            </g>

            {/* bedding + inner sheath */}
            <circle cx="200" cy="200" r={rr(HT_BEDDING.outer)} fill="#1D212B" stroke="#0E1118" strokeWidth="1.5" opacity={dim("bedding")} className="transition-opacity duration-300" />
            <circle cx="200" cy="200" r={rr(HT_BEDDING.inner - 1)} fill="none" stroke="#8B93A5" strokeOpacity="0.35" strokeWidth="4" strokeDasharray="12 4" />
            <circle cx="200" cy="200" r={rr(HT_BEDDING.inner - 4)} fill="#10131B" />

            {/* fillers */}
            <g opacity={dim("bedding")}>
                {HT_FILLERS.map((f, i) => (
                    <circle key={i} cx={f.x} cy={f.y} r={rr(f.r)} fill="#262B37" stroke="#171B25" strokeWidth="1" />
                ))}
            </g>

            {/* three screened cores */}
            {CORE_POS.map(([x, y]) => (
                <g key={x}>
                    {/* copper tape screen */}
                    <circle cx={x} cy={y} r={rr(HT_CORE_R)} fill="none" stroke="#B85C22" strokeWidth="4.5" opacity={dim("insulation")} className="transition-opacity duration-300" />
                    {/* semiconducting insulation screen */}
                    <circle cx={x} cy={y} r={rr(HT_CORE_R - 4)} fill="none" stroke="#0D0F14" strokeWidth="2.5" />
                    {/* XLPE insulation */}
                    <circle cx={x} cy={y} r={rr(HT_CORE_R - 6)} fill="url(#htXlpe)" opacity={dim("insulation")} className="transition-opacity duration-300" />
                    <circle cx={x - 10} cy={y - 12} r={rr((condR + 6) * 0.8)} fill="#FFFFFF" opacity="0.25" />
                    {/* semiconducting conductor screen */}
                    <circle cx={x} cy={y} r={rr(condR + 3)} fill="#14171F" stroke="#000000" strokeWidth="1" />
                    {/* stranded conductor */}
                    <g opacity={dim("conductor")} className="transition-opacity duration-300">
                        {htStrands(x, y, strandR, strandRing).map((s, si) => (
                            <g key={si}>
                                <circle cx={s.x} cy={s.y} r={rr(s.r)} fill="url(#htCu)" stroke="#5C2E0E" strokeWidth="0.7" />
                                <circle cx={s.x} cy={s.y} r={rr(s.r - 3.5)} fill="none" stroke="#8A4517" strokeWidth="0.6" opacity="0.7" />
                            </g>
                        ))}
                    </g>
                </g>
            ))}

            {/* sheen */}
            <circle cx="200" cy="200" r={rr(HT_SHEATH.outer)} fill="url(#htSheen)" pointerEvents="none" />

            {/* hit zones */}
            {ring("sheath", (HT_SHEATH.outer + HT_SHEATH.inner) / 2, HT_SHEATH.outer - HT_SHEATH.inner)}
            {ring("armour", HT_ARMOUR.ring, HT_ARMOUR.wireR * 2.4)}
            {ring("bedding", (HT_BEDDING.outer + HT_BEDDING.inner) / 2, HT_BEDDING.outer - HT_BEDDING.inner)}
            {CORE_POS.map(([x, y], i) => (
                <g key={`hit-${i}`}>
                    <circle
                        cx={x}
                        cy={y}
                        r={rr((HT_CORE_R + condR) / 2 + 2)}
                        fill="none"
                        stroke={active === "insulation" ? "#F0A35E" : "transparent"}
                        strokeOpacity="0.7"
                        strokeWidth={HT_CORE_R - condR - 2}
                        strokeDasharray={active === "insulation" ? "6 4" : undefined}
                        className="transition-all duration-300 cursor-pointer"
                        onMouseEnter={() => onHover("insulation")}
                        onClick={() => onHover("insulation")}
                        style={{ pointerEvents: "stroke" }}
                        data-testid={i === 0 ? "ht-layer-insulation" : undefined}
                    />
                    <circle
                        cx={x}
                        cy={y}
                        r={rr(condR + 2)}
                        fill="transparent"
                        className="cursor-pointer"
                        onMouseEnter={() => onHover("conductor")}
                        onClick={() => onHover("conductor")}
                        style={{ pointerEvents: "all" }}
                        data-testid={i === 0 ? "ht-layer-conductor" : undefined}
                    />
                </g>
            ))}
        </svg>
    );
}

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export default function HTCompare() {
    const [family, setFamily] = useState("11kv");
    const [active, setActive] = useState(0);
    const [layer, setLayer] = useState("armour");
    const fam = FAMILIES[family];
    const t = TYPES[active];
    const layers = layerInfo(t, fam);
    const current = layers.find((l) => l.id === layer);

    useEffect(() => {
        setLayer("armour");
    }, [t.key, family]);

    return (
        <section id="ht-compare" className="relative bg-paper py-20 lg:py-28" data-testid="ht-compare-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-10">
                    <Reveal>
                        <Eyebrow className="mb-5">03 — HT Guide</Eyebrow>
                        <h2 className="font-display font-bold tracking-tight leading-[1.05] text-3xl sm:text-4xl lg:text-5xl text-ink">
                            Three armours. <em className="text-copper">Two voltages.</em>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15} className="md:max-w-sm">
                        <p className="text-mist leading-relaxed">
                            Pick the voltage family, switch the armour tabs, then hover the live
                            cross-section to read every layer.
                        </p>
                    </Reveal>
                </div>

                <Reveal>
                    <div className="flex flex-wrap items-center gap-3 mb-10">
                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-dim mr-2">Voltage family</span>
                        {Object.entries(FAMILIES).map(([key, f]) => (
                            <button
                                key={key}
                                data-testid={`ht-family-${key}`}
                                onClick={() => setFamily(key)}
                                className={`relative rounded-full px-6 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors duration-300 ${
                                    family === key ? "text-white" : "text-mist hover:text-copper border border-line"
                                }`}
                            >
                                {family === key && (
                                    <motion.span
                                        layoutId="ht-family-pill"
                                        className="absolute inset-0 bg-copper rounded-full"
                                        transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                                    />
                                )}
                                <span className="relative z-10">{f.label}</span>
                            </button>
                        ))}
                        <span className="font-mono text-[10px] text-dim tracking-[0.1em] ml-1 hidden md:inline">
                            {fam.note}
                        </span>
                    </div>
                </Reveal>

                {family === "11kv" && (
                    <Reveal>
                        <figure className="rounded-md border border-line bg-white p-3 shadow-sm mb-12" data-testid="ht-figure">
                            <img
                                src={REF_IMG}
                                alt="11 kV XLPE 3-core cable family comparison — FSA, RWA and pressure-extruded sheath"
                                className="w-full rounded-sm"
                            />
                            <figcaption className="font-mono text-[10px] tracking-[0.2em] uppercase text-dim px-2 pt-3 pb-1 flex flex-wrap justify-between gap-2">
                                <span>Ref. Plate — 11 kV XLPE 3-Core Family</span>
                                <span className="text-copper">IS 7098 (Part 2)</span>
                            </figcaption>
                        </figure>
                    </Reveal>
                )}

                <Reveal>
                    <div className="flex flex-wrap gap-2 mb-8">
                        {TYPES.map((ty, i) => (
                            <button
                                key={ty.key}
                                data-testid={`ht-tab-${ty.key}`}
                                onClick={() => setActive(i)}
                                className={`relative rounded-full px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors duration-300 ${
                                    active === i ? "text-white" : "text-mist hover:text-copper border border-line"
                                }`}
                            >
                                {active === i && (
                                    <motion.span
                                        layoutId="ht-tab-pill"
                                        className="absolute inset-0 bg-ink rounded-full"
                                        transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                                    />
                                )}
                                <span className="relative z-10">
                                    {ty.tab} <span className="opacity-60">· {ty.short}</span>
                                </span>
                            </button>
                        ))}
                    </div>
                </Reveal>

                <AnimatePresence mode="wait">
                    <motion.div
                        key={`${family}-${t.key}`}
                        data-testid="ht-panel"
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className="grid lg:grid-cols-2 gap-6 items-stretch mb-14"
                    >
                        <div className="rounded-md bg-inkdark p-6 shadow-[0_24px_60px_rgba(24,23,22,0.2)] flex flex-col">
                            <p className="font-mono text-[9px] tracking-[0.25em] uppercase text-white/40 mb-4">
                                Live cross-section — {fam.label} · hover or tap a layer
                            </p>
                            <div className="w-full max-w-[420px] mx-auto aspect-square">
                                <HTSection typeKey={t.key} thick={family === "33kv"} active={layer} onHover={setLayer} />
                            </div>
                            <div className="flex flex-wrap gap-1.5 mt-5 justify-center">
                                {layers.map((l) => (
                                    <button
                                        key={l.id}
                                        data-testid={`ht-chip-${l.id}`}
                                        onMouseEnter={() => setLayer(l.id)}
                                        onClick={() => setLayer(l.id)}
                                        className={`font-mono text-[9px] uppercase tracking-[0.15em] px-3 py-1.5 rounded-full border transition-colors duration-300 ${
                                            layer === l.id
                                                ? "bg-copper-bright border-copper-bright text-white"
                                                : "border-white/15 text-white/50 hover:text-copper-bright hover:border-copper-bright"
                                        }`}
                                    >
                                        {l.name}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="rounded-md border border-line bg-white p-6 md:p-8 shadow-sm flex flex-col">
                            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-copper mb-2 flex items-center gap-2">
                                <Layers size={12} /> {t.nickname} — {fam.grade}, XLPE
                            </p>
                            <h3 className="font-display font-bold text-2xl md:text-3xl text-ink leading-tight mb-3">
                                {fam.shortSize} × 3 Core — {t.armourTitle}
                            </h3>
                            <p className="text-mist leading-relaxed mb-6">{t.desc}</p>

                            <motion.div
                                key={`${family}-${t.key}-${layer}`}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.35 }}
                                className="rounded-md border border-copper/30 bg-copper-light p-5 mb-6"
                                data-testid="ht-layer-panel"
                            >
                                <p className="font-mono text-[9px] tracking-[0.25em] uppercase text-copper mb-1.5">
                                    Layer — {current.name}
                                </p>
                                <p className="text-sm text-ink leading-relaxed">{current.desc}</p>
                            </motion.div>

                            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 font-mono text-[12px] mb-6">
                                <p className="flex justify-between border-b border-line pb-2">
                                    <span className="text-dim">VOLTAGE</span>
                                    <span className="text-ink text-right">{fam.voltage}</span>
                                </p>
                                <p className="flex justify-between border-b border-line pb-2">
                                    <span className="text-dim">CONDUCTOR</span>
                                    <span className="text-ink text-right">{fam.size}</span>
                                </p>
                                <p className="flex justify-between border-b border-line pb-2">
                                    <span className="text-dim">ARMOUR</span>
                                    <span className="text-ink text-right">{t.armour}</span>
                                </p>
                                <p className="flex justify-between border-b border-line pb-2">
                                    <span className="text-dim">OUTER SHEATH</span>
                                    <span className="text-ink text-right">{t.sheath}</span>
                                </p>
                                <p className="flex justify-between border-b border-line pb-2">
                                    <span className="text-dim">STRENGTH / FLEX</span>
                                    <span className="text-ink text-right">{t.strength} / {t.flex}</span>
                                </p>
                                <p className="flex justify-between border-b border-line pb-2">
                                    <span className="text-dim">STANDARD</span>
                                    <span className="text-ink text-right">IS 7098 (P-2)</span>
                                </p>
                            </div>
                            <div className="mt-auto flex flex-wrap items-center justify-between gap-4">
                                <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-dim">
                                    {t.app}
                                </p>
                                <button
                                    data-testid={`ht-enquire-${t.key}`}
                                    onClick={() => scrollToId("enquiry")}
                                    className="rounded-full bg-copper text-white font-mono uppercase tracking-[0.15em] text-[10px] px-5 py-2.5 hover:bg-copper-bright transition-colors duration-300 flex items-center gap-2"
                                >
                                    Enquire this build <ArrowDownRight size={13} />
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </AnimatePresence>

                <Reveal>
                    <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-dim mb-4 flex items-center gap-2">
                        <ShieldCheck size={12} className="text-copper" /> Same DNA in all three
                    </p>
                    <div className="flex flex-wrap gap-2 mb-12">
                        {[fam.voltage, fam.size, "Stranded Cu / Al conductor", `XLPE insulation, ${fam.grade} grade`, "Semiconducting conductor & core screens", "IS 7098 (Part 2)"].map((s) => (
                            <span
                                key={s}
                                className="font-mono text-[10px] tracking-[0.12em] uppercase bg-white border border-line text-ink px-3 py-1.5 rounded-full"
                            >
                                {s}
                            </span>
                        ))}
                    </div>
                </Reveal>

                <Reveal>
                    <div className="rounded-md border border-line bg-white overflow-hidden shadow-sm overflow-x-auto" data-testid="ht-matrix">
                        <table className="w-full min-w-[720px] text-sm">
                            <thead>
                                <tr className="border-b border-line bg-paper2">
                                    <th className="text-left font-mono text-[10px] uppercase tracking-[0.2em] text-dim px-5 py-4">
                                        What changes
                                    </th>
                                    {TYPES.map((ty) => (
                                        <th key={ty.key} className="text-left font-display font-semibold text-base text-ink px-5 py-4">
                                            {ty.short} <span className="text-dim font-mono text-[9px] uppercase tracking-[0.15em] block mt-0.5">{ty.tab}</span>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {MATRIX.map((row) => {
                                    const differs = new Set(row.values).size > 1;
                                    return (
                                        <tr key={row.label} className="border-b border-line last:border-b-0" data-testid={`ht-matrix-row-${slug(row.label)}`}>
                                            <td className="font-mono text-[10px] uppercase tracking-[0.18em] text-dim px-5 py-4 align-top">
                                                {row.label}
                                            </td>
                                            {row.values.map((v, i) => (
                                                <td
                                                    key={i}
                                                    className={`px-5 py-4 align-top ${differs ? "text-copper font-medium bg-copper-light/50" : "text-ink"}`}
                                                >
                                                    {v}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
